// =====================================================================
// PropertyCuts — Configurable constants
// =====================================================================
// Credit pricing, template registry, render dimensions. Change here only.
// =====================================================================

import type {
  LogoAnimationStyle,
  RenderFormat,
  TemplateId,
  TemplateMeta,
  TransitionStyle,
} from './types';

// ---- Copy -----------------------------------------------------------

export const APP_TAGLINE =
  'Choose landscape or portrait, add your media, and export an MP4. Property details stay optional.';

// ---- Credits --------------------------------------------------------
// One finished video, same price as VideoMotion. A wallet credit is
// valued at ~£0.75 (see STUDIO_CREDIT_VALUE_PENCE). Change the numbers
// here only — the credit RPC is pricing-agnostic.

/** Single-format render (5 credits, same as a finished VideoMotion video) */
export const CREDITS_SINGLE_FORMAT = 5;

/** All-three-formats bundle (split 4+4+4 across rows) */
export const CREDITS_BUNDLE_TOTAL = 12;

/**
 * How a multi-format render splits across the render rows so per-render
 * refunds stay accurate. The three weights sum to CREDITS_BUNDLE_TOTAL.
 */
export const BUNDLE_SPLIT: Record<RenderFormat, number> = {
  vertical: 4,
  square: 4,
  landscape: 4,
};

// ---- Transitions ----------------------------------------------------
// Abstract style names stored on propertycut_projects.transition_style.
// The render worker maps each to a Shotstack transition. 'dissolve' is
// the default and matches the original hardcoded crossfade.

export const DEFAULT_TRANSITION: TransitionStyle = 'dissolve';

export const TRANSITION_OPTIONS: Array<{
  value: TransitionStyle;
  label: string;
  hint: string;
}> = [
  { value: 'dissolve', label: 'Dissolve', hint: 'Soft crossfade' },
  { value: 'cut', label: 'Cut', hint: 'Hard cut, no blend' },
  { value: 'wipe', label: 'Wipe', hint: 'Soft edge sweeps across' },
  { value: 'slide', label: 'Slide', hint: 'Pushes in from the right' },
  { value: 'zoom', label: 'Zoom', hint: 'Punches in over the previous clip' },
  { value: 'reveal', label: 'Reveal', hint: 'A hard edge uncovers the next clip' },
  { value: 'carousel', label: 'Carousel', hint: 'Slides up from below' },
];

// ---- Branding -------------------------------------------------------
// Per-project branding lives on propertycut_projects.agent_data (jsonb).
// The render worker reads these to colour the cards, overlay the logo,
// and play an animated logo bumper at the start / end of the video.

/** Sober slate fallback used when the user hasn't picked a brand colour. */
export const DEFAULT_BRAND_COLOR = '#0f172a';

export const DEFAULT_LOGO_ANIMATION: LogoAnimationStyle = 'fadeIn';

/** How long each logo bumper card holds (seconds). */
export const LOGO_BUMPER_DURATION = 3;

export const LOGO_ANIMATION_OPTIONS: Array<{
  value: LogoAnimationStyle;
  label: string;
  hint: string;
}> = [
  { value: 'fadeIn', label: 'Fade in', hint: 'Logo gently fades up' },
  { value: 'zoomIn', label: 'Zoom in', hint: 'Logo scales up into view' },
  { value: 'slideUp', label: 'Slide up', hint: 'Logo rises from below' },
  { value: 'reveal', label: 'Reveal', hint: 'Logo wipes into view' },
  { value: 'none', label: 'No animation', hint: 'Logo simply appears' },
];

// ---- Render dimensions ----------------------------------------------

export const FORMAT_DIMENSIONS: Record<RenderFormat, { width: number; height: number; label: string }> = {
  vertical: { width: 1080, height: 1920, label: '9:16 — Reels / TikTok' },
  square: { width: 1080, height: 1080, label: '1:1 — Feed' },
  landscape: { width: 1920, height: 1080, label: '16:9 — Rightmove / web' },
};

// ---- Templates ------------------------------------------------------

export const TEMPLATES: Record<TemplateId, TemplateMeta> = {
  'classic-listing': {
    id: 'classic-listing',
    name: 'Classic Listing',
    blurb: 'A polished 60-second walkthrough — title card, exterior, interiors, floor plan, agent outro. Best for Rightmove, Zoopla, agent websites.',
    durationSeconds: 60,
    defaultFormat: 'landscape',
    recommendedMood: 'calm',
    previewLoopSrc: '/templates/classic-listing-loop.mp4',
    recommendedAssetCount: { min: 8, max: 14 },
  },
  'social-reel': {
    id: 'social-reel',
    name: 'Social Reel',
    blurb: 'A 15-second hook with price overlay, fast cuts of the best shots, and a CTA. Built for Instagram Reels, TikTok, Facebook Reels.',
    durationSeconds: 15,
    defaultFormat: 'vertical',
    recommendedMood: 'upbeat',
    previewLoopSrc: '/templates/social-reel-loop.mp4',
    recommendedAssetCount: { min: 4, max: 6 },
  },
  'square-highlight': {
    id: 'square-highlight',
    name: 'Square Highlight',
    blurb: 'A 30-second square edit — hero shot, six interior moments, details card. Versatile for Instagram feed, Facebook feed, LinkedIn.',
    durationSeconds: 30,
    defaultFormat: 'square',
    recommendedMood: 'modern',
    previewLoopSrc: '/templates/square-highlight-loop.mp4',
    recommendedAssetCount: { min: 6, max: 8 },
  },
};

export const TEMPLATE_LIST: TemplateMeta[] = [
  TEMPLATES['classic-listing'],
  TEMPLATES['social-reel'],
  TEMPLATES['square-highlight'],
];

// ---- Storage --------------------------------------------------------

export const STORAGE_BUCKET = 'propertycut-uploads';

export const IMAGEMOTION_LOGO_SRC = '/imagemotion-logo.webp';
export const VIDEOMOTION_LOGO_SRC = '/videomotion-logo.png';
export const HYPERDUSK_LOGO_SRC = '/hyperdusk-logo-wordmark-alpha.png';

// ---- Cross-app links (header nav) -----------------------------------

export const REHUB_APPS: { name: string; href: string }[] = [
  { name: 'PhotoClear', href: 'https://photoclear.studio' },
  { name: 'ImageMotion', href: 'https://imagemotion.studio' },
  { name: 'VideoMotion', href: 'https://videomotion.studio' },
  { name: 'HyperDusk', href: 'https://hyperdusk.studio' },
];
